import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconPlus, IconSearch, IconFileText, IconLoader2, IconLanguage, IconTrash,
  IconList, IconLayoutGrid, IconPencil, IconX, IconDownload, IconPrinter,
} from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { notifications } from '@/lib/notifications';
import aiDocumentService from '../../../services/aiDocumentService';
import PaginationBar from '../../../components/common/PaginationBar';
import SelectField from '../../../components/common/SelectField';
import DatePickerField from '../../../components/common/DatePickerField';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { downloadDocPdf, openPrint } from '../../../utils/aiDocExport';
import { toBanglaDigits } from './aiDocShared';

const PAGE_SIZE = 20;
const toISO = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Icon button with the app's styled tooltip (TooltipProvider is mounted globally).
function TipBtn({ tip, children, ...props }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" {...props}>{children}</button>
      </TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
}

export default function AiDocumentsList({ embedded = false }) {
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslation();
  const { selectedOfficeSpace } = useOfficeSpaceStore();
  const isBn = currentLanguage === 'bn';

  const [docs, setDocs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('aiDocViewMode') || 'list');
  const [sort, setSort] = useState('newest');
  const [language, setLanguage] = useState('');
  const [dateFilter, setDateFilter] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(null); // doc id being exported to PDF

  useEffect(() => {
    const id = setTimeout(() => { setQ(search); setPage(1); }, 400);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    aiDocumentService
      .list({ officeSpaceId: selectedOfficeSpace?.id, page, limit: PAGE_SIZE, search: q, sort, language, date: dateFilter ? toISO(dateFilter) : '' })
      .then((res) => {
        if (!alive) return;
        setDocs(res.data || []);
        setPagination(res.pagination || { page, totalPages: 1, total: (res.data || []).length, limit: PAGE_SIZE });
      })
      .catch((e) => alive && notifications.show({ message: e.message || t('aiDocuments.loadFailed', { defaultValue: 'Failed to load documents.' }), color: 'red' }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [page, q, sort, language, dateFilter, selectedOfficeSpace?.id]);

  const setView = (m) => { setViewMode(m); localStorage.setItem('aiDocViewMode', m); };
  const fmtDate = (d) => {
    try {
      return new Date(d).toLocaleDateString(isBn ? 'bn-BD' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return '';
    }
  };

  async function doDelete() {
    if (!confirmId) return;
    setDeleting(true);
    try {
      await aiDocumentService.remove(confirmId);
      setDocs((prev) => prev.filter((d) => d.id !== confirmId));
      setPagination((p) => ({ ...p, total: Math.max(0, (p.total || 1) - 1) }));
      notifications.show({ message: t('aiDocuments.deleted', { defaultValue: 'Document deleted.' }), color: 'green' });
    } catch (e) {
      notifications.show({ message: e.message || t('aiDocuments.deleteFailed', { defaultValue: 'Delete failed.' }), color: 'red' });
    } finally {
      setDeleting(false);
      setConfirmId(null);
    }
  }

  // PDF / Print need the document body, which the list endpoint omits — fetch it on demand.
  async function loadBlocks(d) {
    const res = await aiDocumentService.get(d.id);
    const doc = res.data?.document;
    let b = Array.isArray(doc?.blocks) ? doc.blocks : [];
    if (doc?.language === 'bn') b = b.map((x) => ({ ...x, text: toBanglaDigits(x.text) }));
    return b;
  }
  async function exportPdf(d) {
    if (exporting) return;
    setExporting(d.id);
    try {
      const blocks = await loadBlocks(d);
      await downloadDocPdf(blocks, { filename: d.name || 'document', isBn: d.language === 'bn' });
    } catch (e) {
      notifications.show({ message: e.message || t('aiDocuments.genFailed', { defaultValue: 'Export failed.' }), color: 'red' });
    } finally {
      setExporting(null);
    }
  }
  async function printDocFn(d) {
    try {
      const blocks = await loadBlocks(d);
      openPrint(blocks, { isBn: d.language === 'bn' });
    } catch (e) {
      notifications.show({ message: e.message || t('aiDocuments.copyFailed', { defaultValue: 'Print failed.' }), color: 'red' });
    }
  }

  const filterTrigger = 'h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-[#1C1B19] dark:text-zinc-100';
  const langLabel = (l) => (l === 'bn' ? 'বাংলা' : 'English');

  return (
    <div className={embedded ? '' : 'max-w-5xl mx-auto px-3 md:px-6 py-4 md:py-6'}>
      {/* Search + New + view toggle */}
      <div className="flex items-center gap-3 mb-3.5">
        <div className="flex-1 flex items-center gap-2.5 h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900">
          <IconSearch size={18} className="text-[#9C988F] dark:text-zinc-400 flex-shrink-0" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('aiDocuments.searchPlaceholder', { defaultValue: 'Search documents…' })}
            className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-[#1C1B19] dark:text-zinc-100 placeholder:text-[#9C988F] dark:placeholder:text-zinc-400"
          />
          {search && (
            <button onClick={() => setSearch('')} className="flex-shrink-0 p-0.5 rounded-full text-[#9C988F] hover:text-[#1C1B19] dark:hover:text-zinc-100">
              <IconX size={16} />
            </button>
          )}
        </div>
        <button
          onClick={() => navigate('/ai-documents/new')}
          className="hidden sm:inline-flex items-center gap-1.5 h-11 px-3.5 rounded-[11px] text-sm font-medium bg-primary hover:bg-primary/90 text-white flex-shrink-0"
        >
          <IconPlus size={16} />
          {t('aiDocuments.newDoc', { defaultValue: 'New document' })}
        </button>
        <div className="flex gap-1 bg-[#F1EFEA] dark:bg-zinc-800 rounded-[10px] p-1 flex-shrink-0">
          <button onClick={() => setView('list')} className={`w-9 h-9 rounded-[7px] flex items-center justify-center transition-colors ${viewMode === 'list' ? 'bg-[#E2702E] text-white' : 'text-[#6E6A63] dark:text-zinc-300 hover:text-[#1C1B19] dark:hover:text-zinc-100'}`}>
            <IconList size={17} />
          </button>
          <button onClick={() => setView('grid')} className={`w-9 h-9 rounded-[7px] flex items-center justify-center transition-colors ${viewMode === 'grid' ? 'bg-[#E2702E] text-white' : 'text-[#6E6A63] dark:text-zinc-300 hover:text-[#1C1B19] dark:hover:text-zinc-100'}`}>
            <IconLayoutGrid size={17} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
        <div>
          <label className="block text-xs text-[#6E6A63] dark:text-zinc-300 mb-1.5">{t('documents.filterByLanguage', { defaultValue: 'Filter by language' })}</label>
          <SelectField
            value={language}
            onChange={(v) => { setLanguage(v); setPage(1); }}
            options={[{ value: '', label: t('templateSets.allLanguages', { defaultValue: 'All languages' }) }, { value: 'bn', label: 'বাংলা' }, { value: 'en', label: 'English' }]}
            triggerClassName={filterTrigger}
          />
        </div>
        <div>
          <label className="block text-xs text-[#6E6A63] dark:text-zinc-300 mb-1.5">{t('documents.filterByDate', { defaultValue: 'Filter by date' })}</label>
          <DatePickerField value={dateFilter} onChange={(d) => { setDateFilter(d); setPage(1); }} clearable disabled={{ after: new Date() }} triggerClassName={filterTrigger} />
        </div>
        <div>
          <label className="block text-xs text-[#6E6A63] dark:text-zinc-300 mb-1.5">{t('common.sortBy', { defaultValue: 'Sort by' })}</label>
          <SelectField
            value={sort}
            onChange={(v) => { setSort(v); setPage(1); }}
            options={[{ value: 'newest', label: t('common.newest', { defaultValue: 'Newest first' }) }, { value: 'oldest', label: t('common.oldest', { defaultValue: 'Oldest first' }) }]}
            triggerClassName={filterTrigger}
          />
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center text-zinc-400"><IconLoader2 className="animate-spin" size={26} /></div>
      ) : docs.length === 0 ? (
        <div className="py-16 flex flex-col items-center gap-3 text-center">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 text-primary"><IconFileText size={26} /></span>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('aiDocuments.empty', { defaultValue: 'No AI documents yet.' })}</p>
          <button onClick={() => navigate('/ai-documents/new')} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium bg-primary hover:bg-primary/90 text-white">
            <IconPlus size={16} />
            {t('aiDocuments.createFirst', { defaultValue: 'Create your first' })}
          </button>
        </div>
      ) : (
        <>
          {viewMode === 'list' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {docs.map((d) => (
                <div key={d.id} className="bg-white dark:bg-zinc-900 border border-[#ECE9E3] dark:border-zinc-700 rounded-[13px] hover:border-[#E2702E]/40 transition-colors">
                  <div className="flex items-center gap-[13px] px-[18px] py-4">
                    <div className="w-10 h-10 rounded-[10px] bg-[#FCEFE7] dark:bg-orange-950/30 text-[#E2702E] flex items-center justify-center flex-shrink-0">
                      <IconFileText size={20} />
                    </div>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/ai-documents/${d.id}`)}>
                      <div className="text-sm font-semibold text-[#1C1B19] dark:text-zinc-100 truncate">{d.name || t('aiDocuments.untitled', { defaultValue: 'Untitled' })}</div>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#B5862B] dark:text-amber-400 bg-[#F7F0E0] dark:bg-amber-950/30 px-2 py-0.5 rounded-[5px] whitespace-nowrap">
                          <IconLanguage size={11} />{langLabel(d.language)}
                        </span>
                        <span className="text-[11.5px] text-[#A8A294] dark:text-zinc-400 whitespace-nowrap">{fmtDate(d.createdAt)}</span>
                      </div>
                    </div>
                    <div className="flex gap-[7px] flex-shrink-0">
                      <TipBtn onClick={() => exportPdf(d)} disabled={exporting === d.id} tip={t('aiDocuments.downloadPdf', { defaultValue: 'Download PDF' })} className="w-8 h-8 border border-[#ECE9E3] dark:border-zinc-700 rounded-lg flex items-center justify-center text-[#6E6A63] dark:text-zinc-300 hover:text-red-600 hover:border-red-300 transition-colors disabled:opacity-50">
                        {exporting === d.id ? <IconLoader2 size={14} className="animate-spin" /> : <IconDownload size={15} />}
                      </TipBtn>
                      <TipBtn onClick={() => printDocFn(d)} tip={t('aiDocuments.print', { defaultValue: 'Print' })} className="w-8 h-8 border border-[#ECE9E3] dark:border-zinc-700 rounded-lg flex items-center justify-center text-[#6E6A63] dark:text-zinc-300 hover:text-purple-600 hover:border-purple-300 transition-colors">
                        <IconPrinter size={15} />
                      </TipBtn>
                      <TipBtn onClick={() => navigate(`/ai-documents/${d.id}`)} tip={t('common.edit', { defaultValue: 'Edit' })} className="w-8 h-8 border border-[#ECE9E3] dark:border-zinc-700 rounded-lg flex items-center justify-center text-[#6E6A63] dark:text-zinc-300 hover:text-[#E2702E] hover:border-[#E2702E]/40 transition-colors">
                        <IconPencil size={15} />
                      </TipBtn>
                      <TipBtn onClick={() => setConfirmId(d.id)} tip={t('aiDocuments.delete', { defaultValue: 'Delete' })} className="w-8 h-8 border border-[#ECE9E3] dark:border-zinc-700 rounded-lg flex items-center justify-center text-[#6E6A63] dark:text-zinc-300 hover:text-red-500 hover:border-red-300 transition-colors">
                        <IconTrash size={15} />
                      </TipBtn>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {docs.map((d) => (
                <div
                  key={d.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/ai-documents/${d.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/ai-documents/${d.id}`); }}
                  className="group text-left bg-white dark:bg-zinc-900 rounded-2xl border border-[#ECE9E3] dark:border-zinc-700 p-4 hover:border-[#E2702E]/40 hover:shadow-sm transition-all flex flex-col gap-2 cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#FCEFE7] dark:bg-orange-950/30 text-[#E2702E] flex-shrink-0"><IconFileText size={18} /></span>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-zinc-400 whitespace-nowrap">{fmtDate(d.createdAt)}</span>
                      <button onClick={(e) => { e.stopPropagation(); setConfirmId(d.id); }} className="p-1 rounded-md text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" title={t('aiDocuments.delete', { defaultValue: 'Delete' })}>
                        <IconTrash size={15} />
                      </button>
                    </div>
                  </div>
                  <p className="font-semibold text-sm text-zinc-900 dark:text-zinc-100 line-clamp-2">{d.name || t('aiDocuments.untitled', { defaultValue: 'Untitled' })}</p>
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-zinc-500 dark:text-zinc-400"><IconLanguage size={12} />{langLabel(d.language)}</span>
                  <div className="flex items-center gap-1.5 mt-1" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => exportPdf(d)} disabled={exporting === d.id} className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-medium bg-red-100 hover:bg-red-200 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-md transition-colors disabled:opacity-50">
                      {exporting === d.id ? <IconLoader2 size={12} className="animate-spin" /> : <IconDownload size={12} />}PDF
                    </button>
                    <button onClick={() => printDocFn(d)} className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-medium bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-md transition-colors">
                      <IconPrinter size={12} />{t('aiDocuments.print', { defaultValue: 'Print' })}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5">
            <PaginationBar
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              pageSize={pagination.limit || PAGE_SIZE}
              currentCount={docs.length}
              onPage={setPage}
            />
          </div>
        </>
      )}

      <ConfirmDialog
        open={!!confirmId}
        onClose={() => setConfirmId(null)}
        onConfirm={doDelete}
        loading={deleting}
        title={t('aiDocuments.deleteTitle', { defaultValue: 'Delete this document?' })}
        description={t('aiDocuments.deleteDesc', { defaultValue: 'This cannot be undone.' })}
        confirmLabel={t('aiDocuments.delete', { defaultValue: 'Delete' })}
      />
    </div>
  );
}
